import { Injectable, inject } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable, { Styles } from 'jspdf-autotable';
import { Borders, Workbook, Worksheet } from 'exceljs';
import { reportConstants, reportConstantsMessage } from '../../common/constants';
import { MSSTReportData } from '../../models/lab-test';
import { MSSTSeparatorResidualOilCompositions, MSSTSeparatorTest, MSSTSeparatorTestVaporCompositions } from '../../msst/models/msst';
import { ReportGeneralUtilityService } from '../report.generalUtility.service';
import { setNumericOrTextCellValue } from './report.cellFormat.util';

@Injectable({
	providedIn: 'root'
})
export class ReportPDFMsstRenderService {
	private _reportGeneralUtilityService = inject(ReportGeneralUtilityService);

	private commonStyles = {
		fontSize: 8,
		textColor: [0, 0, 0],
		fillColor: false,
		lineColor: [0, 0, 0],
		lineWidth: reportConstants.tableLinewidth
	};

	private commonHeadStyles = {
		fontSize: 10,
		halign: reportConstants.center,
		textColor: [0, 0, 0],
		fillColor: false,
		fontStyle: reportConstants.normal,
		font: reportConstants.eMprintCondensedRegular,
		cellPadding: { top: 0.5, bottom: 0.5 },
		lineWidth: 0
	};

	private commonStylesWithoutBorder = {
		fontSize: 8,
		textColor: [0, 0, 0],
		fillColor: false,
		lineColor: [0, 0, 0],
		lineWidth: 0
	};

	private commonHeadStylesWithoutBorder = {
		fontSize: 10,
		halign: reportConstants.center,
		textColor: [0, 0, 0],
		fillColor: false,
		fontStyle: reportConstants.normal,
		font: reportConstants.eMprintCondensedBold,
		cellPadding: { top: 0.5, bottom: 0.5 },
		lineWidth: 0
	};

	private commonBodyStylesWithoutBorder = {
		fontSize: 8,
		halign: reportConstants.center,
		textColor: [0, 0, 0],
		fillColor: false,
		font: reportConstants.eMprintCondensedRegular,
		cellPadding: { top: 0.5, bottom: 0.5 },
		lineWidth: 0
	};

	SeparatorTest(doc: jsPDF, marginSize: number, msstExp: MSSTReportData, appendix = '', wellNameVersion: string, sampleNameVersion: string): void {
		if (msstExp !== undefined && msstExp !== null) {
			doc.addPage();
			let tableStartY = marginSize;
			tableStartY += 20;
			doc.setFontSize(14);
			doc.setFont(reportConstants.eMprintCondensedBold);
			doc.text(appendix + reportConstants.separatorTest, marginSize - 7, tableStartY);
			tableStartY += 5;
			if (appendix !== '') {
				doc.setFontSize(10);
				doc.setFont(reportConstants.eMprintCondensedRegular);
				doc.text(wellNameVersion + ' ' + sampleNameVersion, marginSize - 7, tableStartY);
			}
			tableStartY += 5;
			autoTable(doc, {
				head: reportConstants.separatorHeadingPDF,
				body: msstExp.msstData.separatorTestResult.separatorTestResult.map((val: MSSTSeparatorTest) => [
					val.stage,
					val.temperature,
					this.formatSeparatorValue(val.pressurePsia, 0),
					this.formatSeparatorValueOrDash(val.stageGORScfStb, 2, val.stage, [reportConstants.p_Sat]),
					this.formatSeparatorValue(val.solutionGORScfStb, 1),
					this.formatSeparatorValueOrDash(val.cumulativeGORScfStb, 2, val.stage, [reportConstants.p_Sat]),
					this.formatSeparatorValueOrDash(val.cumulativeGORSatPScfBbl, 2, val.stage, [reportConstants.p_Sat]),
					this.formatSeparatorValue(val.oilFVFBblStb, 4),
					this.formatSeparatorValue(val.shrinkageFactor, 4),
					this.formatSeparatorValue(val.oilDensity, 4),
					this.formatSeparatorValueOrDash(val.gasDeviationFactor, 4, val.stage, [reportConstants.p_Sat]),
					this.formatSeparatorValueOrDash(val.gasFVF, 4, val.stage, [reportConstants.p_Sat, reportConstants.sto]),
					this.formatSeparatorValueOrDash(val.gasGravity, 4, val.stage, [reportConstants.p_Sat])
				]),
				startY: Math.round(tableStartY),
				theme: reportConstants.plain,
				margin: { left: marginSize - 7, right: marginSize - 7 },
				styles: this.commonStylesWithoutBorder as Partial<Styles>,
				headStyles: {
					fontSize: 10,
					halign: reportConstants.center,
					textColor: [0, 0, 0],
					fillColor: false,
					fontStyle: reportConstants.normal,
					font: reportConstants.eMprintCondensedBold,
					cellPadding: { top: 0.5, bottom: 2 },
					lineWidth: 0
				},
				bodyStyles: {
					fontSize: 10,
					halign: reportConstants.center,
					textColor: [0, 0, 0],
					fillColor: false,
					font: reportConstants.eMprintCondensedRegular,
					cellPadding: { top: 0.5, bottom: 0.5 },
					lineWidth: reportConstants.tableLinewidth
				},
				didDrawCell: function (data) {
					const { cell, row, column, doc, section } = data;
					if (section === reportConstants.head) {
						const x = cell.x;
						const y = cell.y;
						const w = cell.width;
						const h = cell.height;

						doc.setDrawColor(0, 0, 0);

						if (row.index === 0) {
							doc.setLineWidth(reportConstants.tableLinewidth);
							doc.line(x, y, x + w, y);
						}

						if (row.index === 3) {
							doc.setLineWidth(reportConstants.tableLinewidth);
							doc.line(x, y + h - 1, x + w, y + h - 1);
							doc.line(x, y + h, x + w, y + h);
						}

						if (column.index === 0) {
							doc.setLineWidth(reportConstants.tableLinewidth);
							doc.line(x, y, x, y + h);
						} else {
							doc.setLineWidth(reportConstants.tableLinewidth);
							doc.line(x, y, x, y + h);
						}

						if (column.index === 12) {
							doc.setLineWidth(reportConstants.tableLinewidth);
							doc.line(x + w, y, x + w, y + h);
						} else {
							doc.setLineWidth(reportConstants.tableLinewidth);
							doc.line(x + w, y, x + w, y + h);
						}
					} else {
						doc.setLineWidth(reportConstants.tableLinewidth);
						doc.rect(cell.x, cell.y, cell.width, cell.height);
					}
				}
			});
			tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
			doc.setFontSize(12);
			doc.setFont(reportConstants.eMprintCondensedRegular);
			const residualOilGravityVal = reportConstants.residualOilGravity + this.formatSeparatorValue(msstExp.msstData.separatorTestResult.residualOilGravityAPI, 1);
			doc.text(residualOilGravityVal, marginSize + 20, tableStartY);
			doc.setFontSize(10);
			tableStartY += 10;
			doc.setFont(reportConstants.eMprintCondensedRegular);
			doc.text(reportConstants.comments, marginSize, tableStartY);
			doc.text(reportConstants.differentialLiberationComments[0], marginSize, tableStartY + 5);
			doc.text(reportConstants.differentialLiberationComments[1], marginSize, tableStartY + 10);
			doc.text(reportConstants.differentialLiberationComments[2], marginSize, tableStartY + 15);
			doc.text(reportConstants.differentialLiberationComments[3], marginSize, tableStartY + 20);
			doc.text(reportConstants.differentialLiberationComments[4], marginSize, tableStartY + 25);
			doc.text(reportConstants.differentialLiberationComments[5], marginSize, tableStartY + 30);
			doc.text(reportConstants.differentialLiberationComments[6], marginSize, tableStartY + 35);
			doc.text(reportConstants.differentialLiberationComments[7], marginSize, tableStartY + 40);
		}
	}

	SeparatorTestAppendix(doc: jsPDF, marginSize: number, msstExp: MSSTReportData): void {
		if (msstExp !== undefined && msstExp !== null) {

			marginSize += 25;
			doc.addPage();
			let tableStartY = marginSize - 15;

			doc.setFontSize(14);
			doc.setFont(reportConstants.eMprintCondensedBold);
			doc.text(reportConstants.separatorTestVaporCompositions, marginSize, tableStartY);
			tableStartY += 5;

			const stageCount = msstExp.msstData.separatorTestResult.separatorTestResult.length - 2;
			type StageKey = `stage${number}` | 'finalStage';
			const stageKeys: StageKey[] = Array.from(
				{ length: stageCount },
				(_, i) => `stage${i + 1}` as StageKey
			);

			const headRows: (string | number)[][] = [
				['', ...stageKeys.map((_, i) => `Stage ${i + 1}`), reportConstants.finalStage],
				[
					reportConstants.pressurePsia,
					...stageKeys.map(key => msstExp.msstData.vaporCompositionResult.vaporPressure[key] ?? ''),
					msstExp.msstData.vaporCompositionResult.vaporPressure.finalStage ?? ''
				],
				[reportConstants.compound, ...stageKeys.map(() => reportConstants.molPercent), reportConstants.molPercent]
			];

			const bodyRows = msstExp.msstData.vaporCompositionResult.gridListData.map((data: MSSTSeparatorTestVaporCompositions) => [
				data.pressure ?? '',
				...stageKeys.map(key => this.formatVaporCompositionValue(data[key], data.pressure)),
				this.formatVaporCompositionValue(data.finalStage, data.pressure)
			]);

			autoTable(doc, {
				head: headRows,
				body: bodyRows,
				startY: Math.round(tableStartY),
				theme: reportConstants.plain,
				margin: { left: marginSize, right: marginSize },
				styles: this.commonStylesWithoutBorder as Partial<Styles>,
				headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
				bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
				didParseCell: function (data) {
					if (data.section === reportConstants.body && data.column.index === 0) {
						data.cell.styles.font = reportConstants.eMprintCondensedBold;
					}
				},
				didDrawCell: function (data) {
					const { cell, row, column, doc, section } = data;

					if (section === reportConstants.head && row.index === 0 && column.index === 0) {
						const x = cell.x, y = cell.y, w = cell.width, h = cell.height;
						doc.setDrawColor(255, 255, 255);
						doc.setLineWidth(reportConstants.tableLinewidth);
						doc.line(x, y, x + w, y);
						doc.line(x, y + h, x + w, y + h);
						doc.line(x, y, x, y + h);
						doc.line(x + w, y, x + w, y + h);
					} else {
						doc.setDrawColor(0, 0, 0);
						doc.setLineWidth(reportConstants.tableLinewidth);
						doc.rect(cell.x, cell.y, cell.width, cell.height);
					}

					if (section === reportConstants.head && row.index === 2) {
						doc.setDrawColor(0, 0, 0);
						doc.setLineWidth(reportConstants.tableLinewidth);
						const x = cell.x, y = cell.y, w = cell.width, h = cell.height;
						doc.line(x, y + h - 0.5, x + w, y + h - 0.5);
						doc.line(x, y + h, x + w, y + h);
						doc.line(x, y, x, y + h);
						doc.line(x + w, y, x + w, y + h);
					}
				}
			});

			tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

			doc.addPage();
			tableStartY = marginSize - 20;
			marginSize += 5;
			doc.setFont(reportConstants.eMprintCondensedBold);
			doc.text(reportConstants.separatorTestResidualOilComposition, marginSize, tableStartY);
			tableStartY += 5;
			autoTable(doc, {
				head: [
					[
						{ content: '', colSpan: 2, styles: { halign: reportConstants.center } },
						{ content: reportConstants.residualOil, colSpan: 2, styles: { halign: reportConstants.center } }
					],
					[
						{ content: reportConstants.compound, colSpan: 2, styles: { halign: reportConstants.center } },
						{ content: reportConstants.weightPercent, styles: { halign: reportConstants.center } },
						{ content: reportConstants.molePercent, styles: { halign: reportConstants.center } }
					]
				],
				body: msstExp.msstData.residualOilCompositionResult.map((val: MSSTSeparatorResidualOilCompositions) => [
					val.component1,
					val.component2,
					this.formatResidualOilCompositionValue(val.weight, val.component1),
					this.formatResidualOilCompositionValue(val.mole, val.component1)
				]),
				startY: Math.round(tableStartY),
				theme: reportConstants.plain,
				margin: { left: marginSize, right: marginSize },
				styles: this.commonStylesWithoutBorder as Partial<Styles>,
				headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
				bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
				didParseCell: function (data) {
					if (data.section === reportConstants.body && data.column.index === 0) {
						data.cell.styles.font = reportConstants.eMprintCondensedBold;
					}
				},
				didDrawCell: function (data) {
					const { cell, row, column, doc, section } = data;
					if (section === reportConstants.head && row.index === 0 && column.index === 0) {
						const x = cell.x;
						const y = cell.y;
						const w = cell.width;
						const h = cell.height;

						doc.setDrawColor(255, 255, 255);
						doc.setLineWidth(reportConstants.tableLinewidth);
						doc.line(x, y, x + w, y);
						doc.line(x, y + h, x + w, y + h);
						doc.line(x, y, x, y + h);
						doc.line(x + w, y, x + w, y + h);
					}
					else {
						doc.setDrawColor(0, 0, 0);
						doc.setLineWidth(reportConstants.tableLinewidth);
						doc.rect(cell.x, cell.y, cell.width, cell.height);
					}
					if (section === reportConstants.head && row.index === 1) {
						doc.setDrawColor(0, 0, 0);
						doc.setLineWidth(reportConstants.tableLinewidth);
						const x = cell.x;
						const y = cell.y;
						const w = cell.width;
						const h = cell.height;
						doc.line(x, y + h - 0.5, x + w, y + h - 0.5);
						doc.line(x, y + h, x + w, y + h);
						doc.line(x, y, x, y + h);
						doc.line(x + w, y, x + w, y + h);
					}
				}
			});
			tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

			doc.addPage();
			marginSize -= 20;
			tableStartY = marginSize;
			doc.text(reportConstants.materialBalanceCalculationSummary, marginSize, tableStartY);
			tableStartY += 10;
			doc.text(reportConstants.massesOfReservoirFluid, marginSize, tableStartY + 3);
			const massesReservoirFluid = msstExp.msstData.materialBalanceSummaryResult.massesReservoirFluid;
			const constTableData = [
				[reportConstants.bcaInitalMass, massesReservoirFluid[0].initialMass, reportConstants.g],
				[reportConstants.bcaMaterialBalanceCalculatedMass, massesReservoirFluid[0].mbCalculatedMass, reportConstants.g],
				[reportConstants.dlDifference, massesReservoirFluid[0].diff, reportConstants.percentage]
			];

			autoTable(doc, {
				head: [['', '', '']],
				body: constTableData,
				startY: Math.round(tableStartY),
				theme: reportConstants.plain,
				margin: { left: marginSize, right: marginSize },
				styles: this.commonStyles as Partial<Styles>,
				headStyles: this.commonHeadStyles as Partial<Styles>,
				bodyStyles: {
					fontSize: 12,
					halign: reportConstants.center,
					textColor: [0, 0, 0],
					fillColor: false,
					font: reportConstants.eMprintCondensedRegular,
					cellPadding: { top: 0.5, bottom: 0.5 },
					lineWidth: reportConstants.tableLinewidth
				},
				columnStyles: {
					0: { cellWidth: 100 },
					1: { cellWidth: 20 },
					2: { cellWidth: 20 }
				},
				didParseCell: function (data) {
					if (data.section === reportConstants.body && data.column.index === 0) {
						data.cell.styles.halign = reportConstants.left;
						data.cell.styles.cellPadding = { left: 2, right: 0, top: 0.5, bottom: 0.5 };
					}
					if (data.section === reportConstants.body && data.column.index === 2) {
						data.cell.styles.halign = reportConstants.left;
					}
					data.cell.styles.cellPadding = { left: 2, right: 2, top: 0.5, bottom: 0.5 };
				}
			});

			tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
			doc.text(reportConstants.massesOfStockTankOil, marginSize, tableStartY + 3);
			const massesStockTankOil = msstExp.msstData.materialBalanceSummaryResult.massesStockTankOil;
			const msstTableData = [
				[reportConstants.bcaMeasuredSTOMass, massesStockTankOil[0].measuredSTOmass, reportConstants.g],
				[reportConstants.bcaMaterialBalanceCalculatedSTOMass, massesStockTankOil[0].calculatedSTOmass, reportConstants.g],
				[reportConstants.dlDifference, massesStockTankOil[0].diff, reportConstants.percentage]
			];
			autoTable(doc, {
				head: [['', '', '']],
				body: msstTableData,
				startY: Math.round(tableStartY),
				theme: reportConstants.plain,
				margin: { left: marginSize, right: marginSize },
				styles: this.commonStyles as Partial<Styles>,
				headStyles: this.commonHeadStyles as Partial<Styles>,
				bodyStyles: {
					fontSize: 12,
					halign: reportConstants.center,
					textColor: [0, 0, 0],
					fillColor: false,
					font: reportConstants.eMprintCondensedRegular,
					cellPadding: { top: 0.5, bottom: 0.5 },
					lineWidth: reportConstants.tableLinewidth
				},
				columnStyles: {
					0: { cellWidth: 100 },
					1: { cellWidth: 20 },
					2: { cellWidth: 20 }
				},
				didParseCell: function (data) {
					if (data.section === reportConstants.body && data.column.index === 0) {
						data.cell.styles.halign = reportConstants.left;
						data.cell.styles.cellPadding = { left: 2, right: 0, top: 0.5, bottom: 0.5 };
					}
					if (data.section === reportConstants.body && data.column.index === 2) {
						data.cell.styles.halign = reportConstants.left;
					}
					data.cell.styles.cellPadding = { left: 2, right: 2, top: 0.5, bottom: 0.5 };
				}
			});
			tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

			doc.text(reportConstants.molesOfReservoirFluid, marginSize, tableStartY + 3);
			const molesReservoirFluid = msstExp.msstData.materialBalanceSummaryResult.molesReservoirFluid;
			const molesTableData = [
				[reportConstants.initialMoles, molesReservoirFluid[0].initialmoles, reportConstants.mol],
				[reportConstants.materialBalanceCalculatedMoles, molesReservoirFluid[0].calculatedmoles, reportConstants.mol],
				[reportConstants.dlDifference, molesReservoirFluid[0].diff, reportConstants.percentage]
			];
			autoTable(doc, {
				head: [['', '', '']],
				body: molesTableData,
				startY: Math.round(tableStartY),
				theme: reportConstants.plain,
				margin: { left: marginSize, right: marginSize },
				styles: this.commonStyles as Partial<Styles>,
				headStyles: this.commonHeadStyles as Partial<Styles>,
				bodyStyles: {
					fontSize: 12,
					halign: reportConstants.center,
					textColor: [0, 0, 0],
					fillColor: false,
					font: reportConstants.eMprintCondensedRegular,
					cellPadding: { top: 0.5, bottom: 0.5 },
					lineWidth: reportConstants.tableLinewidth
				},
				columnStyles: {
					0: { cellWidth: 100 },
					1: { cellWidth: 20 },
					2: { cellWidth: 20 }
				},
				didParseCell: function (data) {
					if (data.section === reportConstants.body && data.column.index === 0) {
						data.cell.styles.halign = reportConstants.left;
						data.cell.styles.cellPadding = { left: 2, right: 0, top: 0.5, bottom: 0.5 };
					}
					if (data.section === reportConstants.body && data.column.index === 2) {
						data.cell.styles.halign = reportConstants.left;
					}
					data.cell.styles.cellPadding = { left: 2, right: 2, top: 0.5, bottom: 0.5 };
				}
			});
			tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
			doc.setFontSize(10);
			doc.setFont(reportConstants.eMprintCondensedRegular);
			doc.text(reportConstants.molesBalanceNote, marginSize, tableStartY);
		}
	}

	async GenerateSeparatorTest(workbook: Workbook, worksheetName: string, msstExp: MSSTReportData, wellName: string, sampleName: string, subHeader: string, appendix = '', subHead = false) {
		try {
			const separatorTestWorksheet = workbook.addWorksheet(worksheetName);

			const borderStyle = {
				top: { style: reportConstants.medium },
				left: { style: reportConstants.medium },
				bottom: { style: reportConstants.medium },
				right: { style: reportConstants.medium }
			};

			separatorTestWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
			this._reportGeneralUtilityService.ApplyFooterAndPageSetup(separatorTestWorksheet, undefined, {
				paperSize: undefined, // letter
				orientation: reportConstants.landscape, //we can set it to 'portrait' or 'landscape'
				fitToPage: false,
				fitToWidth: 0, //page 1
				fitToHeight: 0, // automatic
				scale: 85,
				margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
			});
			separatorTestWorksheet.getColumn(reportConstants.a).width = 3;
			separatorTestWorksheet.getColumn(reportConstants.o).width = 3;

			const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
			const exxonLogoId = workbook.addImage({ base64: exxonLogo, extension: reportConstants.png });

			separatorTestWorksheet.addImage(exxonLogoId, { tl: { col: 0, row: 0 }, ext: { width: 151, height: 31 } });

			separatorTestWorksheet.mergeCells(reportConstants.a4, reportConstants.g4);
			const textReportSubtitleCell = separatorTestWorksheet.getCell(reportConstants.a4);
			textReportSubtitleCell.value = appendix + reportConstants.separatorTest;
			textReportSubtitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
			textReportSubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
			
			if (subHead) {
				separatorTestWorksheet.mergeCells(reportConstants.a5, reportConstants.j5);
				/*Bind Cell C9 for Sub Head */
				const textReportSubtitleCell = separatorTestWorksheet.getCell(reportConstants.a5);
				textReportSubtitleCell.value = wellName + ' ' + sampleName;
				textReportSubtitleCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
				textReportSubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
			}
			this._reportGeneralUtilityService.AddSubHeaderToWorksheet(separatorTestWorksheet, subHeader, reportConstants.c1, reportConstants.n1);

			let rowNo = 7;
			const sampleHead = reportConstants.sampleHeadSeparator;
			const sampleBody = msstExp.msstData.separatorTestResult.separatorTestResult.map((val: MSSTSeparatorTest) => [
				val.stage,
				val.temperature === null ? '' : val.temperature,
				this.formatSeparatorValue(val.pressurePsia, 0),
				this.formatSeparatorValueOrDash(val.stageGORScfStb, 2, val.stage, [reportConstants.p_Sat]),
				this.formatSeparatorValue(val.solutionGORScfStb, 1),
				this.formatSeparatorValueOrDash(val.cumulativeGORScfStb, 2, val.stage, [reportConstants.p_Sat]),
				this.formatSeparatorValueOrDash(val.cumulativeGORSatPScfBbl, 2, val.stage, [reportConstants.p_Sat]),
				this.formatSeparatorValue(val.oilFVFBblStb, 4),
				this.formatSeparatorValue(val.shrinkageFactor, 4),
				this.formatSeparatorValue(val.oilDensity, 4),
				this.formatSeparatorValueOrDash(val.gasDeviationFactor, 4, val.stage, [reportConstants.p_Sat]),
				this.formatSeparatorValueOrDash(val.gasFVF, 4, val.stage, [reportConstants.p_Sat, reportConstants.sto]),
				this.formatSeparatorValueOrDash(val.gasGravity, 4, val.stage, [reportConstants.p_Sat])
			]);

			const tableStartRow = rowNo++;
			this.GenerateTableWithBordersNew(separatorTestWorksheet, sampleHead, sampleBody, tableStartRow, 2, borderStyle);
			rowNo = tableStartRow + sampleHead.length + sampleBody.length;
			rowNo++;

			separatorTestWorksheet.mergeCells(reportConstants.c + rowNo, reportConstants.e + rowNo);
			const textResidualOilGravityCell = separatorTestWorksheet.getCell(reportConstants.c + rowNo);
			textResidualOilGravityCell.value = reportConstants.residualOilGravityAPI;
			textResidualOilGravityCell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
			textResidualOilGravityCell.alignment = { horizontal: reportConstants.right, vertical: reportConstants.middle };
			const valueResidualOilGravityCell = separatorTestWorksheet.getCell(reportConstants.f + rowNo);
			setNumericOrTextCellValue(valueResidualOilGravityCell, this.formatSeparatorValue(msstExp.msstData.separatorTestResult.residualOilGravityAPI, 1));
			valueResidualOilGravityCell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
			valueResidualOilGravityCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

			rowNo += 2;

			this.AddComment(separatorTestWorksheet, reportConstants.c, rowNo, reportConstants.comments); rowNo++;
			this.AddComment(separatorTestWorksheet, reportConstants.c, rowNo, reportConstants.separatorTestComments1); rowNo++;
			this.AddComment(separatorTestWorksheet, reportConstants.c, rowNo, reportConstants.separatorTestComments2); rowNo++;
			this.AddComment(separatorTestWorksheet, reportConstants.c, rowNo, reportConstants.separatorTestComments3); rowNo++;
			this.AddComment(separatorTestWorksheet, reportConstants.c, rowNo, reportConstants.separatorTestComments4); rowNo++;
			this.AddComment(separatorTestWorksheet, reportConstants.c, rowNo, reportConstants.separatorTestComments5); rowNo++;
			this.AddComment(separatorTestWorksheet, reportConstants.c, rowNo, reportConstants.separatorTestComments6); rowNo++;
			this.AddComment(separatorTestWorksheet, reportConstants.c, rowNo, reportConstants.separatorTestComments7); rowNo++;
			this.AddComment(separatorTestWorksheet, reportConstants.c, rowNo, reportConstants.separatorTestComments8);

			rowNo += 2;
			this.AddOuterBorder(separatorTestWorksheet, 3, rowNo, 1, 15);
		}
		catch (error) {
			console.error(reportConstantsMessage.errorGeneratingSheet, error);
		}
	}

	private formatSeparatorValue(value: number | null | undefined, decimalPlaces: number): string {
		return value === null || value === undefined ? '' : value.toFixed(decimalPlaces);
	}

	// Renders '--' for the given stages when the value rounds to zero, otherwise formats normally
	private formatSeparatorValueOrDash(value: number | null | undefined, decimalPlaces: number, stage: string | null | undefined, dashStages: string[]): string {
		if (value === null || value === undefined) return '';
		if (dashStages.includes(stage as string) && Number(value) === 0) return reportConstants.dash;
		return value.toFixed(decimalPlaces);
	}

	async GenerateSeparatorTestVaporCompositions(workbook: Workbook, worksheetName: string, msstExp: MSSTReportData, subHeader: string) {
		try {
			const separatorTestWorksheet = workbook.addWorksheet(worksheetName);
			const borderStyle = { top: { style: reportConstants.thin }, left: { style: reportConstants.thin }, bottom: { style: reportConstants.thin }, right: { style: reportConstants.thin } };
			separatorTestWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
			this._reportGeneralUtilityService.ApplyFooterAndPageSetup(separatorTestWorksheet, undefined, {
				paperSize: undefined, // letter
				orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
				fitToPage: true,
				fitToWidth: 1, //page 1
				fitToHeight: 1, // automatic
				scale: 100,
				margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
			});
			separatorTestWorksheet.getColumn(reportConstants.b).width = 5;

			const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
			const exxonLogoId = workbook.addImage({ base64: exxonLogo, extension: reportConstants.png });
			separatorTestWorksheet.mergeCells(reportConstants.a1, reportConstants.b2);
			separatorTestWorksheet.addImage(exxonLogoId, { tl: { col: 0, row: 0 }, ext: { width: 151, height: 31 } });

			this._reportGeneralUtilityService.AddSubHeaderToWorksheet(separatorTestWorksheet, subHeader, reportConstants.c1, reportConstants.h2);

			separatorTestWorksheet.mergeCells(reportConstants.a5, reportConstants.g5);
			separatorTestWorksheet.getRow(5).height = 15;
			const textReportSubtitleCell = separatorTestWorksheet.getCell(reportConstants.a5);
			textReportSubtitleCell.value = reportConstants.separatorTestVaporCompositions;
			textReportSubtitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
			textReportSubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

			const stageCount = msstExp.msstData.separatorTestResult.separatorTestResult.filter((s: MSSTSeparatorTest) => ![reportConstants.p_Sat, reportConstants.sto].includes(s.stage)).length;
			const stageHeaderRow = [''].concat(Array.from({ length: stageCount }, (_, i) => `Stage ${i + 1}`)).concat([reportConstants.finalStage]);
			const vaporPressureData = msstExp.msstData.vaporCompositionResult.vaporPressure as MSSTSeparatorTestVaporCompositions;
			const pressureRow = [reportConstants.pressurePsia.toString()].concat(Array.from({ length: stageCount }, (_, i) => vaporPressureData[`stage${i + 1}`]?.toString() || '')).concat([vaporPressureData.finalStage?.toString() || '']);
			const moleHeaderRow = [reportConstants.component.toString()].concat(Array(stageCount).fill(reportConstants.molePercent)).concat([reportConstants.molePercent]);
			const tableHeader = [stageHeaderRow, pressureRow, moleHeaderRow];
			const tableBody = msstExp.msstData.vaporCompositionResult.gridListData.map((data: MSSTSeparatorTestVaporCompositions) => [data.pressure !== undefined ? data.pressure : ''].concat(Array.from({ length: stageCount }, (_, i) => this.formatVaporCompositionValue(data[`stage${i + 1}`], data.pressure))).concat([this.formatVaporCompositionValue(data.finalStage, data.pressure)]));
			const columnWidth = [18].concat(Array(stageCount).fill(10)).concat([14]);
			const tableStartRow = 7;
			const tableStartColumn = 2;

			tableHeader.forEach((headerRow, idx) => {
				headerRow.forEach((header, colIdx) => {
					const cell = separatorTestWorksheet.getCell(tableStartRow + idx, tableStartColumn + colIdx);
					if (idx === 1 && colIdx > 0) {
						setNumericOrTextCellValue(cell, header);
					} else {
						cell.value = header;
					}
					cell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
					cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle, wrapText: true };
					if (idx > 0 || colIdx > 0) cell.border = borderStyle as Partial<Borders>;
					separatorTestWorksheet.getColumn(tableStartColumn + colIdx).width = columnWidth[colIdx];
					separatorTestWorksheet.getRow(tableStartRow + idx).height = 15;
				});
			});

			tableBody.forEach((row: string[], rowIdx: number) => {
				row.forEach((cellValue: string, colIdx: number) => {
					const cell = separatorTestWorksheet.getCell(tableStartRow + tableHeader.length + rowIdx, tableStartColumn + colIdx);
					setNumericOrTextCellValue(cell, cellValue);
					cell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: (colIdx === 0) };
					cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
					cell.border = (rowIdx === 0)
						? { top: { style: reportConstants.double }, left: borderStyle.left, bottom: borderStyle.bottom, right: borderStyle.right } as Partial<Borders>
						: borderStyle as Partial<Borders>;
					separatorTestWorksheet.getRow(tableStartRow + rowIdx).height = 15;
				});
			});
		}
		catch (error) {
			console.error(reportConstantsMessage.errorGeneratingSheet, error);
		}
	}

	private formatVaporCompositionValue(value: number | string | null | undefined, rowLabel: string | null | undefined): string {
		if (value === null || value === undefined || value === '') return '';
		const numericValue = Number(value);
		if (Number.isNaN(numericValue)) return String(value);
		const decimalPlaces = rowLabel === reportConstants.totals ? 2 : 3;
		return numericValue.toFixed(decimalPlaces);
	}

	private formatResidualOilCompositionValue(value: number | null | undefined, rowLabel: string | null | undefined): string {
		return value === null || value === undefined ? '' : value.toFixed(rowLabel === reportConstants.totals ? 2 : 3);
	}

	async GenerateSeparatorTestResidualOilComp(workbook: Workbook, worksheetName: string, msstExp: MSSTReportData, subHeader: string) {
		try {
			const sepTestResOilCompWorksheet = workbook.addWorksheet(worksheetName);
			const borderStyle = { top: { style: reportConstants.thin }, left: { style: reportConstants.thin }, bottom: { style: reportConstants.thin }, right: { style: reportConstants.thin } };
			sepTestResOilCompWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
			this._reportGeneralUtilityService.ApplyFooterAndPageSetup(sepTestResOilCompWorksheet, undefined, {
				paperSize: undefined, // letter
				orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
				fitToPage: true,
				fitToWidth: 1, //page 1
				fitToHeight: 1, // automatic
				scale: 68,
				margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
			});

			const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
			const exxonLogoId = workbook.addImage({ base64: exxonLogo, extension: reportConstants.png });
			sepTestResOilCompWorksheet.mergeCells(reportConstants.a1, reportConstants.b2);
			sepTestResOilCompWorksheet.addImage(exxonLogoId, { tl: { col: 0, row: 0 }, ext: { width: 151, height: 31 } });

			this._reportGeneralUtilityService.AddSubHeaderToWorksheet(sepTestResOilCompWorksheet, subHeader, reportConstants.c1, reportConstants.g2);
			sepTestResOilCompWorksheet.mergeCells(reportConstants.b4, reportConstants.g4);
			sepTestResOilCompWorksheet.getRow(4).height = 15;
			const textReportSubtitleCell = sepTestResOilCompWorksheet.getCell(reportConstants.b4);
			textReportSubtitleCell.value = reportConstants.separatorTestResidualOilComposition;
			textReportSubtitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
			textReportSubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

			const tableHeader = [[{ content: '', colSpan: 2, styles: { halign: reportConstants.center } }, { content: reportConstants.residualOil, colSpan: 2, styles: { halign: reportConstants.center } }], [{ content: reportConstants.component, colSpan: 2, styles: { halign: reportConstants.center } }, { content: reportConstants.weightPercent, styles: { halign: reportConstants.center } }, { content: reportConstants.molePercent, styles: { halign: reportConstants.center } }]];
			const tableBody = msstExp.msstData.residualOilCompositionResult.map((val: MSSTSeparatorResidualOilCompositions) => [
				val.component1,
				val.component2,
				this.formatResidualOilCompositionValue(val.weight, val.component1),
				this.formatResidualOilCompositionValue(val.mole, val.component1)
			]);
			const tableStartRow = 6;
			const tableStartColumn = 2;
			const columnWidth = [15, 25, 15, 15];
			for (let colIdx = 0; colIdx < 4; colIdx++) sepTestResOilCompWorksheet.getColumn(tableStartColumn + colIdx).width = columnWidth[colIdx];

			tableHeader.forEach((headerRow, idx) => {
				let colSpan = 0;
				headerRow.forEach((header, colIdx) => {
					if (header.colSpan && header.colSpan > 1) {
						sepTestResOilCompWorksheet.mergeCells(tableStartRow + idx, tableStartColumn + colIdx + colSpan, tableStartRow + idx, tableStartColumn + colIdx + colSpan + header.colSpan - 1);
						colSpan += header.colSpan - 1;
					}
					const cell = sepTestResOilCompWorksheet.getCell(tableStartRow + idx, tableStartColumn + colIdx + colSpan);
					cell.value = header.content;
					cell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
					cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle, wrapText: true };
					if (idx > 0 || colIdx > 0) cell.border = borderStyle as Partial<Borders>;
					sepTestResOilCompWorksheet.getRow(tableStartRow + idx).height = 25;
				});
			});

			tableBody.forEach((row: (string | number)[], rowIdx: number) => {
				row.forEach((cellValue: string | number, colIdx: number) => {
					const cell = sepTestResOilCompWorksheet.getCell(tableStartRow + tableHeader.length + rowIdx, tableStartColumn + colIdx);
					setNumericOrTextCellValue(cell, cellValue);
					cell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: (colIdx < 2) };
					cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
					cell.border = (rowIdx === 0) ? { top: { style: reportConstants.double }, left: borderStyle.left, bottom: borderStyle.bottom, right: borderStyle.right } as Partial<Borders> : borderStyle as Partial<Borders>;
					sepTestResOilCompWorksheet.getRow(tableStartRow + rowIdx).height = 16;
				});
			});

			
		}
		catch (error) {
			console.error(reportConstantsMessage.errorGeneratingSheet, error);
		}
	}

	async GenerateMaterialBalanceSummary(workbook: Workbook, worksheetName: string, msstExp: MSSTReportData, subHeader: string) {
		try {
			const materialBalanceSummaryWorksheet = workbook.addWorksheet(worksheetName);
			const borderStyle = { top: { style: reportConstants.thin }, left: { style: reportConstants.thin }, bottom: { style: reportConstants.thin }, right: { style: reportConstants.thin } };
			materialBalanceSummaryWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
			this._reportGeneralUtilityService.ApplyFooterAndPageSetup(materialBalanceSummaryWorksheet, undefined, {
				paperSize: undefined, // letter
				orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
				fitToPage: false,
				fitToWidth: 0,
				fitToHeight: 0,
				scale: 100,
				margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
			});

			const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
			const exxonLogoId = workbook.addImage({ base64: exxonLogo, extension: reportConstants.png });
			materialBalanceSummaryWorksheet.mergeCells(reportConstants.a1, reportConstants.b2);
			materialBalanceSummaryWorksheet.addImage(exxonLogoId, { tl: { col: 0, row: 0 }, ext: { width: 151, height: 31 } });

			this._reportGeneralUtilityService.AddSubHeaderToWorksheet(materialBalanceSummaryWorksheet, subHeader, reportConstants.c1, reportConstants.i2);

			materialBalanceSummaryWorksheet.mergeCells(reportConstants.a5, reportConstants.f5);
			materialBalanceSummaryWorksheet.getRow(5).height = 15;
			const textReportSubtitleCell = materialBalanceSummaryWorksheet.getCell(reportConstants.a5);
			textReportSubtitleCell.value = reportConstants.materialBalanceCalculationSummary;
			textReportSubtitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
			textReportSubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

			let rowNo = 7;
			const tableStartColumn = 1;
			const massesReservoirFluid = msstExp.msstData.materialBalanceSummaryResult.massesReservoirFluid;
			const massesReservoirFluidData = [[reportConstants.bcaInitalMass, massesReservoirFluid[0].initialMass, reportConstants.g], [reportConstants.bcaMaterialBalanceCalculatedMass, massesReservoirFluid[0].mbCalculatedMass, reportConstants.g], [reportConstants.dlDifference, massesReservoirFluid[0].diff, reportConstants.percentage]];
			GenerateSummaryTable(materialBalanceSummaryWorksheet, rowNo, reportConstants.massesOfReservoirFluid, massesReservoirFluidData, tableStartColumn, borderStyle);
			rowNo += massesReservoirFluidData.length + 2;

			const massesStockTankOil = msstExp.msstData.materialBalanceSummaryResult.massesStockTankOil;
			const massesStockTankOilData = [[reportConstants.bcaMeasuredSTOMass, massesStockTankOil[0].measuredSTOmass, reportConstants.g], [reportConstants.bcaMaterialBalanceCalculatedSTOMass, massesStockTankOil[0].calculatedSTOmass, reportConstants.g], [reportConstants.dlDifference, massesStockTankOil[0].diff, reportConstants.percentage]];
			GenerateSummaryTable(materialBalanceSummaryWorksheet, rowNo, reportConstants.massesOfStockTankOil, massesStockTankOilData, tableStartColumn, borderStyle);
			rowNo += massesStockTankOilData.length + 2;

			const molesReservoirFluid = msstExp.msstData.materialBalanceSummaryResult.molesReservoirFluid;
			const molesReservoirFluidData = [[reportConstants.initialMoles, molesReservoirFluid[0].initialmoles, reportConstants.moles], [reportConstants.materialBalanceCalculatedMoles, molesReservoirFluid[0].calculatedmoles, reportConstants.moles], [reportConstants.dlDifference, molesReservoirFluid[0].diff, reportConstants.percentage]];
			GenerateSummaryTable(materialBalanceSummaryWorksheet, rowNo, reportConstants.molesOfReservoirFluid, molesReservoirFluidData, tableStartColumn, borderStyle);
			rowNo += molesReservoirFluidData.length + 2;

			materialBalanceSummaryWorksheet.mergeCells(rowNo, tableStartColumn, rowNo + 1, tableStartColumn + 6);
			const massesReservoirFluidCell = materialBalanceSummaryWorksheet.getCell(rowNo, tableStartColumn);
			massesReservoirFluidCell.value = reportConstants.molesBalanceNote;
			massesReservoirFluidCell.font = { name: reportConstants.eMprintCondensed, size: 10, bold: false, italic: true };
			massesReservoirFluidCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle, wrapText: true };
			rowNo++;

			rowNo += 2;
			
		}
		catch (error) {
			console.error(reportConstantsMessage.errorGeneratingSheet, error);
		}

		function GenerateSummaryTable(worksheet: Worksheet, rowNo: number, title: string, tableData: (string | number)[][], tableStartColumn: number, borderStyle: { top: { style: string; }; left: { style: string; }; bottom: { style: string; }; right: { style: string; }; }) {
			worksheet.mergeCells(rowNo, tableStartColumn, rowNo, tableStartColumn + 5);
			const massesReservoirFluidCell = worksheet.getCell(rowNo, tableStartColumn);
			massesReservoirFluidCell.value = title;
			massesReservoirFluidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
			massesReservoirFluidCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
			rowNo++;

			tableData.forEach((row: (string | number)[], rowIdx: number) => {
				let colSpan = 0;
				row.forEach((cellValue: string | number, colIdx: number) => {
					if (colIdx === 0) {
						worksheet.mergeCells(rowNo + rowIdx, tableStartColumn + colIdx + colSpan, rowNo + rowIdx, tableStartColumn + colIdx + colSpan + 5);
						colSpan += 5;
					}
					const cell = worksheet.getCell(rowNo + rowIdx, tableStartColumn + colIdx + colSpan);
					const isNumber = !isNaN(Number(cellValue)) && cellValue !== '';
					setNumericOrTextCellValue(cell, cellValue);
					cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
					cell.alignment = { horizontal: isNumber ? reportConstants.center : reportConstants.left, vertical: reportConstants.middle,wrapText: true };
					cell.border = borderStyle as Partial<Borders>;
					worksheet.getRow(rowNo + rowIdx).height = 25;
				});
			});
		}
	}

	public AddOuterBorder(worksheet: Worksheet, startRowNo: number, endRowNo: number, startColumnNo: number, endColumnNo: number) {
		for (let r = startRowNo; r <= endRowNo; r++) {
			for (let c = startColumnNo; c <= endColumnNo; c++) {
				const cell = worksheet.getCell(r, c);
				const currentTop = cell.border && cell.border.top ? cell.border.top : undefined;
				const currentBottom = cell.border && cell.border.bottom ? cell.border.bottom : undefined;
				const currentLeft = cell.border && cell.border.left ? cell.border.left : undefined;
				const currentRight = cell.border && cell.border.right ? cell.border.right : undefined;

				cell.border = {
					top: r === startRowNo ? { style: reportConstants.thin } : currentTop,
					bottom: r === endRowNo ? { style: reportConstants.thin } : currentBottom,
					left: c === startColumnNo ? { style: reportConstants.thin } : currentLeft,
					right: c === endColumnNo ? { style: reportConstants.thin } : currentRight,
				};
			}
		}

	}

	GenerateTableWithBordersNew(worksheet: Worksheet, tableHeader: string[][], tableBody: (string | number)[][], tableStartRow: number, tableStartColumn: number, borderStyle: { top: { style: string; }; left: { style: string; }; bottom: { style: string; }; right: { style: string; } }) {
		const columnWidth = reportConstants.separatorTestColumnWidth;
		tableHeader.forEach((headerRow, idx) => {
			headerRow.forEach((header, colIdx) => {
				const cell = worksheet.getCell(tableStartRow + idx, tableStartColumn + colIdx);
				cell.value = header;
				cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
				cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle, wrapText: true };
				cell.border = { top: idx === 0 ? { style: reportConstants.medium } : borderStyle.top, bottom: idx === tableHeader.length - 1 ? { style: reportConstants.double } : borderStyle.bottom, left: colIdx === 0 ? { style: reportConstants.medium } : borderStyle.left, right: colIdx === headerRow.length - 1 ? { style: reportConstants.medium } : borderStyle.right } as Partial<Borders>;
				worksheet.getColumn(tableStartColumn + colIdx).width = columnWidth[colIdx];
				worksheet.getRow(tableStartRow + idx).height = 80;
			});
		});

		tableBody.forEach((row, rowIdx) => {
			row.forEach((cellValue, colIdx) => {
				const cell = worksheet.getCell(tableStartRow + tableHeader.length + rowIdx, tableStartColumn + colIdx);
				setNumericOrTextCellValue(cell, cellValue);
				cell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
				cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
				cell.border = { bottom: rowIdx === tableBody.length - 1 ? { style: reportConstants.medium } : borderStyle.bottom, left: colIdx === 0 ? { style: reportConstants.medium } : borderStyle.left, right: colIdx === row.length - 1 ? { style: reportConstants.medium } : borderStyle.right } as Partial<Borders>;
			});
		});
	}

	AddComment(worksheet: Worksheet, column: string, rowNo: number, value: string, fontSize = 11) {
		const textCommentsCell = worksheet.getCell(column + rowNo);
		textCommentsCell.value = value;
		textCommentsCell.font = { name: reportConstants.eMprintCondensed, size: fontSize, bold: false };
		textCommentsCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
		worksheet.getRow(rowNo).height = 15;
	}
}
